import { useState } from 'react';
import { api } from '../../lib/api';

const VOCAB = [
  'Industrial buyer',
  'Retail buyer',
  'Land buyer',
  'Office buyer',
  '1031 investor',
  'Owner-user',
  'Industrial tenant',
  'Landlord',
  'Seller / owner',
  'Developer',
  'Broker / referral',
  'Unknown',
];

interface PersonaData {
  persona_label?: string | null;
  persona_role?: string | null;
  persona_intent?: string | null;
  persona_detail?: { buying?: string; selling?: string; flags?: string[] } | null;
  persona_confidence?: number | null;
  persona_status?: 'suggested' | 'confirmed' | null;
}

export default function DealIntentBlock({
  ghlId,
  persona,
  onChange,
}: {
  ghlId: string;
  persona: PersonaData | null;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [overrideLabel, setOverrideLabel] = useState('');

  const d = persona?.persona_detail || {};

  const reSuggest = async () => {
    setBusy(true);
    try {
      await api.suggestPersona(ghlId);
      onChange();
    } catch (e: any) {
      console.error('Re-suggest failed:', e?.message);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    const labelToConfirm = overrideLabel || persona?.persona_label;
    if (!labelToConfirm) return;
    setBusy(true);
    try {
      await api.setPersona(ghlId, {
        persona_label: labelToConfirm,
        persona_role: overrideLabel ? undefined : persona?.persona_role,
        persona_intent: overrideLabel ? undefined : persona?.persona_intent,
        persona_detail: overrideLabel ? undefined : persona?.persona_detail,
      });
      setOverrideLabel('');
      onChange();
    } catch (e: any) {
      console.error('Confirm persona failed:', e?.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border border-gray-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500 uppercase tracking-wide">Deal intent</span>
        {persona?.persona_confidence != null && (
          <span className="text-[10px] text-gray-400">
            conf {(persona.persona_confidence * 100).toFixed(0)}% · {persona.persona_status || 'unset'}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span
          className={`text-xs px-2 py-0.5 rounded font-medium ${
            persona?.persona_status === 'confirmed'
              ? 'bg-info-wash text-info'
              : persona?.persona_label
              ? 'bg-gray-100 text-gray-600'
              : 'bg-gray-50 text-gray-400'
          }`}
        >
          {persona?.persona_label || 'No persona yet'}
        </span>
        {persona?.persona_role === 'both' && (
          <span className="text-[10px] px-2 py-0.5 rounded bg-success-wash text-success font-medium">
            buy + sell
          </span>
        )}
      </div>

      {persona?.persona_intent && (
        <p className="text-xs text-gray-600 mb-2 italic">{persona.persona_intent}</p>
      )}

      {d.buying && (
        <div className="text-xs text-gray-700 mb-1">
          <span className="text-success font-medium">Buying:</span> {d.buying}
        </div>
      )}
      {d.selling && (
        <div className="text-xs text-gray-700 mb-1">
          <span className="text-warning font-medium">Selling:</span> {d.selling}
        </div>
      )}
      {Array.isArray(d.flags) && d.flags.length > 0 && (
        <div className="text-xs text-gray-500 mb-2">Flags: {d.flags.join(' · ')}</div>
      )}

      <div className="flex gap-2 mt-2 flex-wrap">
        <select
          value={overrideLabel}
          onChange={(e) => setOverrideLabel(e.target.value)}
          className="flex-1 min-w-0 text-xs border border-gray-300 rounded px-2 py-1 bg-white"
        >
          <option value="">Override label…</option>
          {VOCAB.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
        <button
          disabled={busy || (!overrideLabel && !persona?.persona_label)}
          onClick={confirm}
          className="text-xs px-3 py-1 border border-info/30 rounded text-info hover:bg-info-wash disabled:opacity-50"
        >
          Confirm
        </button>
        <button
          disabled={busy}
          onClick={reSuggest}
          title="Re-suggest from notes"
          className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
        >
          {busy ? '…' : '↻'}
        </button>
      </div>
    </div>
  );
}
